import type { WorkflowEdge, WorkflowNode } from '@/types/workflow'

/**
 * Nodes the simulation has not reached yet, in breadth-first order from the
 * nodes already on the timeline. The walker reveals steps lazily (one hop at a
 * time, branches only after a choice), so the sidebar used to show only the
 * steps walked so far. These are rendered as muted, non-clickable previews so
 * the whole workflow is visible from the start.
 *
 * Pure: no React, no network. Nodes already in `walkedNodeIds` are excluded, as
 * is any node reachable only through a cycle back into the walked set.
 */
export function computeUpcomingNodes(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  walkedNodeIds: string[],
): WorkflowNode[] {
  const byId = new Map(nodes.map(n => [n.id, n]))
  const adj = new Map<string, string[]>()
  for (const e of edges) {
    const list = adj.get(e.source)
    if (list) list.push(e.target)
    else adj.set(e.source, [e.target])
  }

  const seen = new Set(walkedNodeIds)
  const queue = [...walkedNodeIds]
  const out: WorkflowNode[] = []
  while (queue.length > 0) {
    const id = queue.shift()!
    for (const target of adj.get(id) || []) {
      if (seen.has(target)) continue
      seen.add(target)
      const node = byId.get(target)
      if (!node) continue
      out.push(node)
      queue.push(target)
    }
  }
  return out
}
